"use client";

import { Call, Sms } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { Page } from "@/components/core";
import { Skeleton, SocialIcon } from "@/components/ui";
import { $api } from "@/lib/api-client";
import { detectSocialLinkName } from "@/lib/utils/social-link.utils";
import { ShopAboutDrawer } from "./ShopAboutDrawer";
import { ShopDetailBanner } from "./ShopDetailBanner";
import { ShopItemsList } from "./ShopItemsList";
import { ShopLocation } from "./ShopLocation";

interface ShopDetailProps {
  shopId: string;
}

const ShopDetailSkeleton = () => {
  return (
    <Page
      width="default"
      outsideChildren={
        <div className="w-full flex justify-center">
          <div className="w-full max-w-screen-md mx-auto h-44 bg-bg-secondary border-b-2 border-dashed border-stroke-light relative mb-14">
            {/* Logo skeleton - matches ShopDetailBanner dimensions (88x88) */}
            <Skeleton
              variant="muted"
              className="bg-bg-tertiary rounded-full border-2 border-stroke-primary absolute right-4 -bottom-1/4 w-[88px] h-[88px]"
            />
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Content skeleton will be added here as UI is implemented */}
      </div>
    </Page>
  );
};

export const ShopDetail = ({ shopId }: ShopDetailProps) => {
  const t = useTranslations("shop");
  const shop = $api.useQuery("get", "/vendor/{id}", {
    params: {
      path: { id: shopId },
    },
  });

  if (shop.isLoading) {
    return <ShopDetailSkeleton />;
  }

  if (!shop.data?.data) {
    return (
      <Page width="default">
        <div className="text-text-secondary text-sm text-center py-8">
          {t("detail.notFound")}
        </div>
      </Page>
    );
  }

  const isVerified = shop.data.data.is_verified === true;

  return (
    <Page
      width="default"
      outsideChildren={
        <div>
          <ShopDetailBanner
            shop_name={shop.data?.data?.shop_name || ""}
            logo_url={shop.data?.data?.logo_url || ""}
            banner_url={
              shop.data?.data?.vendor_categories[0]?.vendor_category
                ?.banner_url || ""
            }
          />
        </div>
      }
      bottomChildren={
        <div className="w-full max-w-screen-lg mx-auto">
          <ShopItemsList
            shopId={shopId}
            activity_type={shop.data.data.activity_type}
          />
        </div>
      }
    >
      <div>
        <div className="space-y-5">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">
              {shop.data?.data?.shop_name}
            </h3>
            <ShopLocation
              latitude={shop.data?.data?.latitude}
              longitude={shop.data?.data?.longitude}
            />

            <div className="flex flex-col space-y-2">
              {shop.data?.data?.phone_number && (
                <div className="flex items-center gap-2">
                  <Call size={16} />
                  <span className="text-sm mt-0.5">
                    {shop.data.data.phone_number}
                  </span>
                </div>
              )}
              {shop.data?.data?.phone_number &&
                shop.data?.data?.owner_email && (
                  <div className="flex items-center gap-2">
                    <Sms size={16} />
                    <span className="text-sm mt-0.5">
                      {shop.data.data.owner_email}
                    </span>
                  </div>
                )}
              {!shop.data?.data?.phone_number &&
                shop.data?.data?.owner_email && (
                  <div className="flex items-center gap-2">
                    <Sms size={16} />
                    <span className="text-sm mt-0.5">
                      {shop.data.data.owner_email}
                    </span>
                  </div>
                )}
              {shop.data?.data?.vendor_social_links?.map((link) => {
                if (!link?.url) return null;
                const socialName = detectSocialLinkName(link.url);
                const url = link.url.startsWith("http")
                  ? link.url
                  : `https://${link.url}`;

                return (
                  <a
                    key={link.id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <SocialIcon name={socialName} size={16} />
                    <span className="text-sm mt-0.5">{link.url}</span>
                  </a>
                );
              })}
            </div>
          </div>

          {!isVerified && (
            <div className="bg-semantic-error/10 border border-semantic-error rounded-sm p-3">
              <p className="text-sm font-medium text-semantic-error">
                {t("detail.unverifiedWarning")}
              </p>
            </div>
          )}

          <div
            className="border border-stroke-subtle rounded-sm p-4"
            style={{
              boxShadow:
                "0px 4px 8px -4px #5A636B0A, 0px 4px 24px -4px #5A636B1A",
            }}
          >
            <div className="flex items-center justify-between w-full">
              <h3 className="font-semibold text-sm">{t("detail.aboutShop")}</h3>
              {shop.data?.data && <ShopAboutDrawer shopData={shop.data.data} />}
            </div>
            <p className="text-sm text-text-secondary mt-3 line-clamp-3">
              {shop.data?.data?.description}
            </p>
          </div>
        </div>
      </div>
    </Page>
  );
};
